import { ChevronDownIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  Tooltip,
  useColorModeValue
} from '@chakra-ui/react';
import LocationSelectorForBidMultiplier from 'components/adspresso/LocationSelectorForBidMultiplier';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { IoAddCircleOutline } from 'react-icons/io5';
import { formatNumber, truncateString } from 'utils/functions';

const POSITION_GROUPS = {
  Facebook: [
    'facebook_feed',
    'facebook_marketplace',
    'facebook_suggested_video',
    'facebook_right_hand_column',
    'facebook_biz_disco_feed',
    'facebook_story',
    'facebook_facebook_reels',
    'facebook_instream_video',
    'facebook_facebook_reels_overlay',
    'facebook_search',
  ],
  Instagram: [
    'instagram_stream',
    'instagram_profile_feed',
    'instagram_explore',
    'instagram_explore_home',
    'instagram_story',
    'instagram_reels',
    'instagram_ig_search',
  ],
  Messenger: ['messenger_messenger_home', 'messenger_story'],
  AudienceNetwork: [
    'audience_network_classic',
    'audience_network_rewarded_video',
  ],
};

const POSITION_LABELS = {
  facebook_feed: 'Facebook Feed',
  facebook_marketplace: 'Facebook Marketplace',
  facebook_suggested_video: 'Facebook Suggested Video',
  facebook_right_hand_column: 'Facebook Right Hand Column',
  facebook_biz_disco_feed: 'Facebook Biz Disco Feed',
  facebook_story: 'Facebook Story',
  facebook_facebook_reels: 'Facebook Facebook Reels',
  facebook_instream_video: 'Facebook Instream Video',
  facebook_facebook_reels_overlay: 'Facebook Facebook Reels Overlay',
  facebook_search: 'Facebook Search',
  instagram_stream: 'Instagram Stream',
  instagram_profile_feed: 'Instagram Profile Feed',
  instagram_explore: 'Instagram Explore',
  instagram_explore_home: 'Instagram Explore Home',
  instagram_story: 'Instagram Story',
  instagram_reels: 'Instagram Reels',
  instagram_ig_search: 'Instagram IG Search',
  messenger_messenger_home: 'Messenger Home',
  messenger_story: 'Messenger Story',
  audience_network_classic: 'Audience Network Classic',
  audience_network_rewarded_video: 'Audience Network Rewarded Video',
};

const OS_OPTIONS = ['Android', 'iOS'];
const AGE_OPTIONS = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];

const OPTIONS = [
  { key: "Location", label: "Location", description: "Choose specific cities, countries, regions/states" },
  { key: "OS", label: "Mobile operating system", description: "Choose iOS or Android." },
  { key: "Placement", label: "Placement", description: "Choose specific placements, such as Instagram Feed or Facebook Reels." }
];

const AGE_TO_RANGE = {
  '18-24': '18-24',
  '25-34': '25-34',
  '35-44': '35-44',
  '45-54': '45-54',
  '55-64': '55-64',
  '65+':   '65-100',
};

const RANGE_TO_AGE = {
  '18-24': '18-24',
  '25-34': '25-34',
  '35-44': '35-44',
  '45-54': '45-54',
  '55-64': '55-64',
  '65-100': '65+',
};

const toApiAgeKey = (label) => AGE_TO_RANGE[label] ?? label;

const getOptionLabel = (opt) => POSITION_LABELS[opt] || opt.charAt(0).toUpperCase() + opt.slice(1);

const BidMultiplier = ({ preFilledData, adsAccountId, onBidChange }) => {

  const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.600');
  const prevPayloadRef = useRef();
  const [openIndices, setOpenIndices] = useState([0]);

  const [groups, setGroups] = useState([{ ages: [], baseMultiplier: 1.0, genderCriteria: {} }]);

  const ageSource = preFilledData?.user_groups?.age ?? {};
  const ageHash = useMemo(() => JSON.stringify(ageSource), [ageSource]);

  useEffect(() => {
    if (!preFilledData?.user_groups?.age) return;
    const ageData = preFilledData.user_groups.age;
    const newGroups = [];

    const isAndroidOrIOS = (key) =>
      key?.toLowerCase() === "android" || key?.toLowerCase() === "ios";

    const addUnique = (arr, item) => {
      if (!arr.includes(item)) arr.push(item);
    };

    const extractLocations = (obj, type, fullArr, restored) => {
      Object.entries(obj).forEach(([key, locVal]) => {
        if (key === "default") return;

        let multiplier = 1;

        if (typeof locVal === "number") {
          multiplier = locVal;
        } else if (typeof locVal === "object") {
          // Check for deeply nested user_os
          if (locVal.user_os && typeof locVal.user_os === "object") {
            const platformKey = Object.keys(locVal.user_os).find(isAndroidOrIOS);
            if (platformKey) {
              const platformData = locVal.user_os[platformKey];

              // If number, it's just a multiplier
              if (typeof platformData === "number") {
                multiplier = platformData;
                restored.os = platformKey;
                addUnique(restored.selected, "OS");
              }

              // If object, check for position_type and multiplier
              if (typeof platformData === "object") {
                if (platformData.position_type) {
                  restored.placement = platformData.position_type;
                  addUnique(restored.selected, "Placement");
                }

                if (typeof platformData.multiplier === "number") {
                  multiplier = platformData.multiplier;
                }

                restored.os = platformKey;
                addUnique(restored.selected, "OS");
              }
            }
          } else if (locVal.position_type) {
            // check for direct position_type
            restored.placement = locVal.position_type;
            addUnique(restored.selected, "Placement");
          }
        }

        fullArr.push({ key, multiplier, type });
      });
    };

    const normalizeGenderCriteria = (gVal) => {
      const restored = {
        multiplier: 0.9,
        selected: [],
      };

      if (typeof gVal === "number") {
        restored.multiplier = gVal;
        return restored;
      }

      if (gVal?.multiplier !== undefined) {
        restored.multiplier = gVal.multiplier ?? 0.9;
        restored.selected = gVal.selected || [];
        restored.location = gVal.location;
        restored.locationFull = gVal.locationFull || [];
        restored.placement = gVal.placement;
      }

      // Handle home_location
      if (gVal?.home_location) {
        restored.location = gVal.home_location;
        const fullArr = [];

        if (gVal.home_location.cities) {
          extractLocations(gVal.home_location.cities, "city", fullArr, restored);
        }
        if (gVal.home_location.regions) {
          extractLocations(gVal.home_location.regions, "region", fullArr, restored);
        }
        if (gVal.home_location.countries) {
          extractLocations(gVal.home_location.countries, "country", fullArr, restored);
        }

        restored.locationFull = fullArr;
        if (fullArr.length > 0) addUnique(restored.selected, "Location");
      }

      // Direct position_type
      if (gVal?.position_type) {
        restored.placement = gVal.position_type;
        addUnique(restored.selected, "Placement");
      }

      // Direct user_os
      if (gVal?.user_os && typeof gVal.user_os === "object") {
        const platformKey = Object.keys(gVal.user_os).find(isAndroidOrIOS);
        if (platformKey) {
          const platformData = gVal.user_os[platformKey];

          if (typeof platformData === "number") {
            restored.os = platformKey;
            addUnique(restored.selected, "OS");
          } else if (typeof platformData === "object") {
            if (platformData.position_type) {
              restored.placement = platformData.position_type;
              addUnique(restored.selected, "Placement");
            }

            if (typeof platformData.multiplier === "number") {
              restored.multiplier = platformData.multiplier;
            }

            restored.os = platformKey;
            addUnique(restored.selected, "OS");
          }
        }
      }

      return restored;
    };

    Object.entries(ageData).forEach(([ageKey, value]) => {
      if (ageKey === "default") return;

      const uiAgeKey = RANGE_TO_AGE[ageKey] || ageKey;
      const group = {
        ages: [uiAgeKey],  
        baseMultiplier: 1.0,
        genderCriteria: {},
      };
      if (typeof value === "number") {
        group.baseMultiplier = value;
      } else if (value?.gender) {
        Object.entries(value.gender).forEach(([gender, gVal]) => {
          if (gender === "default") return;

          const restored = normalizeGenderCriteria(gVal);
          group.genderCriteria[gender] = restored;
        });
      }

      newGroups.push(group);
    });

    // only set when different 
    const curr = JSON.stringify(groups);
    const next = JSON.stringify(newGroups);
    if (curr !== next) setGroups(newGroups);
  }, [ageHash]);
  
  useEffect(() => {
    const agePayload = {};
  
    groups.forEach(({ ages, baseMultiplier, genderCriteria }) => {
      ages.forEach((age) => {
        const apiAgeKey = toApiAgeKey(age);
        const genderPayload = {};
        Object.keys(genderCriteria).forEach((gender) => {
          genderPayload[gender] = buildGenderPayload(genderCriteria[gender]);
        });
  
        const hasGenderData = Object.keys(genderPayload).some(
          (key) => key === 'male' || key === 'female'
        );
  
        // use apiAgeKey instead of UI label
        agePayload[apiAgeKey] = hasGenderData
          ? { gender: genderPayload }
          : baseMultiplier;
      });
    });
  
    // Always send even if empty to clear parent
    const finalPayload =
      Object.keys(agePayload).length > 0
        ? { user_groups: { age: agePayload } }
        : { user_groups: { age: {} } };
  
    const newPayloadStr = JSON.stringify(finalPayload);
    if (prevPayloadRef.current !== newPayloadStr) {
      prevPayloadRef.current = newPayloadStr;
      onBidChange?.(finalPayload);
    }
  }, [groups, onBidChange]);
  
  const usedAges = groups.flatMap((g) => g.ages);

  const addGroup = () => {
    if (usedAges.length >= AGE_OPTIONS.length) return;
  
    setGroups((prevGroups) => {
      const newGroups = [...prevGroups, { ages: [], baseMultiplier: 1.0, genderCriteria: {} }];
  
      setOpenIndices((prevOpenIndices) => [...prevOpenIndices, newGroups.length - 1]);
  
      return newGroups;
    });
  };
  

  const deleteGroup = (indexToRemove) => {
    const updated = groups.filter((_, i) => i !== indexToRemove);
    setGroups(updated);
  };
  

  const handleAgeToggle = (groupIndex, age) => {
    const updated = [...groups];
    const group = updated[groupIndex];
  
    const isSelected = group.ages.includes(age);
  
    // Toggle age selection
    group.ages = isSelected
      ? group.ages.filter((a) => a !== age)
      : [...group.ages, age];
  
    // If no ages selected, reset baseMultiplier and genderCriteria
    if (group.ages.length === 0) {
      group.baseMultiplier = 1.0;
      group.genderCriteria = {};
    }
  
    setGroups(updated);
  };
  
  const handleBaseMultiplierChange = (groupIndex, value) => {
    const updated = [...groups];
    updated[groupIndex].baseMultiplier = value;
    setGroups(updated);
  };

  const handleGenderCriteriaChange = (groupIndex, gender, updatedCriteria) => {
    let cleaned = { ...updatedCriteria };
    const selected = cleaned.selected || [];
  
    // Trust user intent — keep selected items even if empty
    const finalSelected = [...selected];
  
    // Clean up data (optional, but do NOT remove from selected)
    if (!cleaned.location || Object.keys(cleaned.location).length === 0) {
      delete cleaned.location;
      delete cleaned.locationFull;
    }
  
    if (!cleaned.placement || !Object.values(cleaned.placement).some(Boolean)) {
      delete cleaned.placement;
    }
  
    if (!cleaned.os || cleaned.os === '') {
      delete cleaned.os;
      delete cleaned.osRange;
    }
  
    cleaned.selected = finalSelected;
  
    const updated = [...groups];
    updated[groupIndex].genderCriteria[gender] = cleaned;
    setGroups(updated);
  };

  const resetGenderCriteria = (groupIndex, gender) => {
    setGroups(prev => {
      const updated = [...prev];
      const current = updated[groupIndex].genderCriteria[gender] || {};
      // Recreate a clean object: keep multiplier (fallback 0.9), wipe everything else
      updated[groupIndex].genderCriteria[gender] = {
        multiplier: current.multiplier ?? 0.9,
        selected: [],
      };
      return updated;
    });
  };


  return (
    <Box>
      {groups.map((group, groupIndex) => {
        const isOpen = openIndices.includes(groupIndex);
  
        const toggleGroup = (index) => {
          setOpenIndices((prev) => {
            if (prev.includes(index)) {
              return prev.filter((i) => i !== index);
            } else {
              return [...prev, index];
            }
          });
        };
  
        const formatAges = (ages) => {
          if (ages.length === 0) return '';
          const parseAgeRange = (range) => {
            const match = range.match(/\d+/);
            return match ? parseInt(match[0], 10) : Infinity;
          };
          const sortedAges = [...ages].sort((a, b) => parseAgeRange(a) - parseAgeRange(b));
          if (sortedAges.length === 1) return `${sortedAges[0]}`;
          if (sortedAges.length === 2) return `${sortedAges[0]} and ${sortedAges[1]}`;
          return `${sortedAges.slice(0, -1).join(', ')} and ${sortedAges[sortedAges.length - 1]}`;
        };
  
        const selectedAges = formatAges(group.ages);
  
        const selectedGenders = Object.entries(group.genderCriteria)
          .filter(([_, value]) => value.selected)
          .map(([gender]) => gender);
  
        return (
          <Box
            key={groupIndex}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            mb={2}
          >
            {/* Header */}
            <Flex
              align="center"
              justify="space-between"
              px={4}
              py={2}
              fontWeight="bold"
              cursor="pointer"
              onClick={() => toggleGroup(groupIndex)}
            >
              <Box>{group.ages.length > 0 ? `Age (${selectedAges})` : 'Age'}</Box>
              <ChevronDownIcon
                boxSize={5}
                transform={isOpen ? 'scaleY(-1)' : 'scaleY(1)'}
                transition="transform 0.1s ease-in-out"
              />
            </Flex>
  
            {/* Panel */}
            <Collapse in={isOpen} animateOpacity>
              <Box px={4} pb={4} pt={2}>
                {/* Age Selector */}
                <Menu
                  closeOnSelect={false}
                  onOpen={() => {
                    if (!isOpen) toggleGroup(groupIndex);
                  }}
                >
                  <MenuButton
                    as={Button}
                    size="sm"
                    width="100%"
                    variant="outline"
                    fontWeight="normal"
                    bg={inputBackground}
                    _active={{ background: inputBackground, borderColor: 'blue.500' }}
                    _hover={{ background: inputBackground, borderColor: 'gray.300' }}
                  >
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm">
                        {group.ages.length > 0 ? selectedAges : 'Select age range'}
                      </Text>
                      <ChevronDownIcon boxSize={5} />
                    </Flex>
                  </MenuButton>
                  <MenuList maxHeight="200px" overflowY="auto">
                    {AGE_OPTIONS.map((age) => {
                      const isUsedElsewhere = usedAges.includes(age) && !group.ages.includes(age);
                      const isChecked = group.ages.includes(age);
                      return (
                        <MenuItem
                          key={age}
                          onClick={() => {
                            if (!isUsedElsewhere) handleAgeToggle(groupIndex, age);
                          }}
                          isDisabled={isUsedElsewhere}
                          _hover={{ bg: hoverBgColor }}
                        >
                          <Checkbox
                            colorScheme="brand"
                            isChecked={isChecked}
                            isDisabled={isUsedElsewhere}
                            pointerEvents="none"
                          >
                            <Text fontSize="sm">{age}</Text>
                          </Checkbox>
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
  
                {/* Base Multiplier */}
                {group.ages.length > 0 && Object.keys(group.genderCriteria).length === 0 && (
                  <Flex align="center" mt={4} gap={4}>
                    {/* Slider container */}
                    <Flex align="center" flex={'95%'}>
                      <Slider
                        min={0.09}
                        max={1.0}
                        step={0.01}
                        value={group.baseMultiplier}
                        onChange={(val) => handleBaseMultiplierChange(groupIndex, val)}
                        colorScheme="brand"
                        flex="1"
                      >
                        <SliderTrack>
                          <SliderFilledTrack />
                        </SliderTrack>
                        <Tooltip
                          hasArrow
                          placement="top"
                          label={formatNumber(group.baseMultiplier)}
                          bg="gray.700"
                          color="white"
                          fontSize="xs"
                        >
                          <SliderThumb />
                        </Tooltip>
                      </Slider>
                    </Flex>
                  
                    {/* Text container */}
                    <Flex flex={'5%'} justify="center">
                      <Text fontSize="12px">{formatNumber(group.baseMultiplier)}</Text>
                    </Flex>
                  </Flex>
                )}
  
                {/* Gender Selector */}
                <Flex mt={4}>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      isDisabled={!group.ages.length}
                      size="sm"
                      width="100%"
                      variant="outline"
                      fontWeight="normal"
                      bg={inputBackground}
                      _active={{ background: inputBackground, borderColor: 'blue.500' }}
                      _hover={{ background: inputBackground, borderColor: 'gray.300' }}
                    >
                      <Flex width="100%" justifyContent="space-between" alignItems="center">
                        <Text fontSize="sm">
                          {selectedGenders.length > 0
                            ? selectedGenders
                                .map((g) => g.charAt(0).toUpperCase() + g.slice(1))
                                .join(' and ')
                            : 'Select gender'}
                        </Text>
                        <ChevronDownIcon boxSize={5} />
                      </Flex>
                    </MenuButton>
                    <MenuList>
                      {['male', 'female'].map((gender) => {
                        const isChecked = group.genderCriteria[gender] !== undefined;
                        return (
                          <MenuItem
                            key={gender}
                            onClick={() => {
                              const updated = [...groups];
                              if (isChecked) {
                                delete updated[groupIndex].genderCriteria[gender];
                              } else {
                                updated[groupIndex].genderCriteria[gender] = {
                                  multiplier: 0.9,
                                  selected: [],
                                };
                              }
                              setGroups(updated);
                            }}
                            _hover={{ bg: hoverBgColor }}
                          >
                            <Checkbox
                              colorScheme="brand"
                              isChecked={isChecked}
                              pointerEvents="none"
                            >
                              <Text fontSize="sm">{gender.charAt(0).toUpperCase() + gender.slice(1)}</Text>
                            </Checkbox>
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Menu>
                </Flex>
  
                {/* Gender Details */}
                {Object.keys(group.genderCriteria).map((gender) => {
                  const gVal = group.genderCriteria[gender];
                  const hasCriteria = (gVal.selected || []).length > 0;
  
                  return (
                    <Box key={gender} mt={4} p={3} borderWidth="1px" borderRadius="md">
                      <Text fontWeight="semibold" fontSize="sm">
                        {gender.charAt(0).toUpperCase() + gender.slice(1)}
                      </Text>
  
                      <Box mt={2}>
                        {!hasCriteria && (
                          <Flex align="center" gap={4}>
                            {/* Slider container */}
                            <Flex align="center" flex="95%">
                              <Slider
                                colorScheme="brand"
                                min={0.09}
                                max={1.0}
                                step={0.01}
                                value={gVal.multiplier ?? 0.9}
                                onChange={(val) => {
                                  const updated = [...groups];
                                  updated[groupIndex].genderCriteria[gender] = {
                                    ...gVal,
                                    multiplier: val,
                                  };
                                  setGroups(updated);
                                }}
                                flex="1"
                              >
                                <SliderTrack>
                                  <SliderFilledTrack />
                                </SliderTrack>
                                <Tooltip
                                  hasArrow
                                  placement="top"
                                  label={formatNumber(gVal.multiplier ?? 0.9)}
                                  bg="gray.700"
                                  color="white"
                                  fontSize="xs"
                                >
                                  <SliderThumb />
                                </Tooltip>
                              </Slider>
                            </Flex>

                            {/* Text container */}
                            <Flex flex="5%" justify="center">
                              <Text fontSize="12px">{formatNumber(gVal.multiplier ?? 0.9)}</Text>
                            </Flex>
                          </Flex>
                        )}

                        <GenderCriteria
                          adsAccountId={adsAccountId}
                          value={gVal}
                          onChange={(val) => handleGenderCriteriaChange(groupIndex, gender, val)}
                        />
  
                        {hasCriteria && (
                          <Button
                            mt={3}
                            leftIcon={<HiOutlineRefresh size={15} />}
                            size="sm"
                            variant="outline"
                            onClick={() => resetGenderCriteria(groupIndex, gender)}
                          >
                            Reset
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
  
                {groupIndex !== 0 && (
                  <Flex justify="flex-end" mt={4}>
                    <Button
                      size="sm"
                      leftIcon={<DeleteIcon />}
                      colorScheme="gray"
                      variant="outline"
                      onClick={() => deleteGroup(groupIndex)}
                    >
                      Remove
                    </Button>
                  </Flex>
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
  
      {/* Add Group Button */}
      {usedAges.length > 0 &&
        usedAges.length < AGE_OPTIONS.length &&
        groups.every((group) => group.ages.length > 0) && (
          <Button
            mt={6}
            onClick={addGroup}
            size="sm"
            variant="outline"
            colorScheme="gray"
            leftIcon={<IoAddCircleOutline size={15} />}
          >
            Add Age Group
          </Button>
        )}
    </Box>
  );
};

const GenderCriteria = ({ value, onChange, adsAccountId }) => {
  const [activeCriteria, setActiveCriteria] = React.useState(null);

  const handleSelectCriteria = (opt) => {
    let newSelected = value.selected || [];
    if (!newSelected.includes(opt)) {
      newSelected = [...newSelected, opt];
    }
    let newValue = { ...value, selected: newSelected };
    if (opt === "Placement" && !value.placement) newValue.placement = {};
    if (opt === "OS" && !value.os) newValue.os = "";
    if (opt === "Location" && !value.location) newValue.location = {};
    onChange(newValue);
    setActiveCriteria(opt);
  };

  return (
    <Box  mt={1}>
      {(value.selected || []).map((opt) => {
        const key = opt.toLowerCase();
        let displayValue = '';

        if (key === 'os') {
          displayValue = value.os || '';
        } else if (key === 'placement') {
          const selectedKeys = Object.keys(value.placement || {}).filter(k => value.placement[k]);
          displayValue = selectedKeys.map(getOptionLabel).join(', ');
        } else if (key === 'location') {
          const locationNames = (value.locationFull || []).map(loc => loc.name).join(', ');
          displayValue = locationNames;
        }

        // Skip if no display value
        if (!displayValue) return null;

        return (
          <Text key={opt} fontSize="11px">
            {opt} ({displayValue})
          </Text>
        );
      })}

    <Menu>
      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="sm" mt={2}>
        Choose Criteria
      </MenuButton>
      <MenuList>
        {OPTIONS.map(({ key, label, description }) => (
          <MenuItem key={key} onClick={() => handleSelectCriteria(key)}>
            <Box>
              <Text fontWeight="bold" fontSize="13px">{label}</Text>
              <Text fontSize="12px" color="gray.500">{description}</Text>
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>


    {(value.selected || []).includes("Location") && (
    <Box display={activeCriteria === "Location" ? "block" : "none"}>
      <LocationSelectorForBidMultiplier
        adsAccountId={adsAccountId}
        onLocationsChange={({ payload, fullSelectedLocations }) =>
          onChange({ ...value, location: payload, locationFull: fullSelectedLocations })
        }
        value={value}
      />
    </Box>
  )}


    {(value.selected || []).includes("Placement") && (
      <Box display={activeCriteria === "Placement" ? "block" : "none"}>
        <PlacementSelector
          selected={value.placement || {}}
          onChange={(pl) => onChange({ ...value, placement: pl })}
        />
      </Box>
    )}

    {(value.selected || []).includes("OS") && (
      <Box display={activeCriteria === "OS" ? "block" : "none"} mt={4}>
        <Select
          placeholder="Select mobile operating system"
          value={value.os || ""}
          onChange={(e) => onChange({ ...value, os: e.target.value })}
          size="sm"
          borderRadius="md"
        >
          {OS_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>

        {(value.os && !value?.placement) && (
          <Flex align="center" mt={2} gap={4}>
            <Flex flex={'95%'} align="center">
              <Slider
                colorScheme="brand"
                min={0.09}
                max={1.0}
                step={0.01}
                value={value.osRange ?? 0.9}
                onChange={(val) => onChange({ ...value, osRange: val })}
                flex="1"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <Tooltip
                  hasArrow
                  placement="top"
                  label={formatNumber(value.osRange ?? 0.9)}
                  bg="gray.700"
                  color="white"
                  fontSize="xs"
                >
                  <SliderThumb />
                </Tooltip>
              </Slider>
            </Flex>

            <Flex flex={'5%'} justify="center">
              <Text fontSize="12px">{formatNumber(value.osRange ?? 0.9)}</Text>
            </Flex>
          </Flex>
        )}
      </Box>
    )}
    </Box>
  );
};

const PlacementSelector = ({ selected = {}, onChange }) => {

  const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.600');
  
  const togglePlacement = (opt) => {
    if (opt in selected) {
      const newSelected = { ...selected };
      delete newSelected[opt];
      onChange(newSelected);
    } else {
      onChange({ ...selected, [opt]: 1.0 });
    }
  };
  const updateMultiplier = (opt, val) => {
    onChange({ ...selected, [opt]: val });
  };
  return (
    <Box mt={3}>
      <Menu closeOnSelect={false}>
        <MenuButton
          as={Button}
          size="sm"
          width="100%"
          variant="outline"
          fontWeight="normal"
          bg={inputBackground}
          _active={{background: inputBackground, borderColor:'blue.500'}}
          _hover={{background: inputBackground, borderColor:'gray.300'}}
        >
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          <Text fontSize="sm">
            {truncateString(
              Object.keys(selected).length > 0
                ? Object.keys(selected)
                    .map(getOptionLabel)
                    .join(', ')
                : 'Select placements',
              60
            )}
          </Text>
          <ChevronDownIcon boxSize={5}/>
        </Flex>
        </MenuButton>
        <MenuList maxHeight="300px" overflowY="auto" p={2} w="full">
          {Object.entries(POSITION_GROUPS).map(([groupName, options]) => (
            <Box key={groupName} mb={3}  width="100%">
              <Text fontWeight="bold" mb={1} px={2}>{groupName}</Text>
              {options.map((opt) => {
                const isChecked = opt in selected;

                return (
                  <Box
                    key={opt}
                    display="flex"
                    alignItems="center"
                    px={2}
                    py={1}
                    cursor="pointer"
                    _hover={{ bg: hoverBgColor }}
                    onClick={() => togglePlacement(opt)}
                  >
                    <Checkbox
                      colorScheme="brand"
                      isChecked={isChecked}
                      pointerEvents="none" // Click passes through to Box
                      mr={2}
                    />
                    <Text fontSize="sm">{getOptionLabel(opt)}</Text>
                  </Box>
                );
              })}
            </Box>
          ))}
        </MenuList>
      </Menu>
      <Box maxHeight="300px" overflowY="auto" px={4}>
        {Object.keys(selected).map((opt) => (
          <Flex key={opt} align="center" mt={3} gap={2}>
            {/* Label */}
            <Text fontSize="12px" width="140px">
              {truncateString(getOptionLabel(opt), 20)}
            </Text>

            {/* Slider container */}
            <Flex flex="1" align="center" mr={2}>
              <Slider
                min={0.09}
                max={1.0}
                step={0.01}
                value={selected[opt] ?? 1.0}
                onChange={(val) => updateMultiplier(opt, val)}
                colorScheme="brand"
                flex="1"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <Tooltip
                  hasArrow
                  placement="top"
                  bg="gray.700"
                  color="white"
                  fontSize="xs"
                  label={formatNumber(selected[opt] ?? 1.0)}
                >
                  <SliderThumb />
                </Tooltip>
              </Slider>
            </Flex>

            {/* Value display */}
            <Flex width="20px" justify="center">
              <Text fontSize="12px">{formatNumber(selected[opt] ?? 1.0)}</Text>
            </Flex>
          </Flex>

        ))}
      </Box>
    </Box>
  );
};

const buildGenderPayload = (criteria) => {
  const selected = criteria.selected ?? [];

  const hasLocation = selected.includes('Location');
  const hasOS = selected.includes('OS');
  const hasPlacement = selected.includes('Placement');

  const os = criteria.os;
  const osRange = criteria.osRange ?? 0.9;
  const rawPositions = criteria.placement ?? {};
  const location = criteria.location ?? {};
  const multiplier = criteria.multiplier ?? 1.0;

  const validOS = ['iOS', 'Android'];
  const isValidOS = validOS.includes(os);
  const hasValidPlacement = Object.values(rawPositions).some(Boolean);

  // Wrapping utilities
  const wrapInPosition = (value) => hasPlacement && hasValidPlacement ? { position_type: rawPositions } : value;

  const wrapInOS = (value) => (hasOS && isValidOS) ? { user_os: { [os]: value } } : value;

  const applyAllWrappers = (val) => {
    let actualValue = val;

    // Use osRange if OS is selected and Placement is NOT valid
    if (hasOS && isValidOS && (!hasPlacement || !hasValidPlacement)) {
      actualValue = osRange;
    }
    // Placement wrapping comes before OS wrapping
    actualValue = wrapInPosition(actualValue);
    actualValue = wrapInOS(actualValue);

    return actualValue;
  };

  //  Handle location-specific values
  if (hasLocation) {
    const { cities = {}, regions = {}, countries = {} } = location;

    const hasValidCities = Object.keys(cities).length > 0;
    const hasValidRegions = Object.keys(regions).length > 0;
    const hasValidCountries = Object.keys(countries).length > 0;

    if (hasValidCities || hasValidRegions || hasValidCountries) {
      const nestedLocation = {};

      if (hasValidCities) {
        nestedLocation.cities = {};
        for (const cityId in cities) {
          const val = cities[cityId]; // actual multiplier
          nestedLocation.cities[cityId] = applyAllWrappers(val);
        }
      }

      if (hasValidRegions) {
        nestedLocation.regions = {};
        for (const regionId in regions) {
          const val = regions[regionId];
          nestedLocation.regions[regionId] = applyAllWrappers(val);
        }
      }

      if (hasValidCountries) {
        nestedLocation.countries = {};
        for (const countryId in countries) {
          const val = countries[countryId];
          nestedLocation.countries[countryId] = applyAllWrappers(val);
        }
      }

      return { home_location: nestedLocation };
    }
  }

  //  No location — apply wrappers to the global value
  return applyAllWrappers(multiplier);
};

export default BidMultiplier;
